// Presentation table for the Earnings Calendar. Maps the backend's
// pre-grouped days into a professional per-day card + table layout. Pure
// presentation: all data fetching / state lives in <EarningsWidget>.

import type { DailyEarnings, MarketType } from '../api/useEarningsCalendar';
import {
    formatCurrency,
    formatDateHeader,
    formatReportingTime,
} from '../utils/formatters';

interface EarningsTableProps {
    days: DailyEarnings[];
    market: MarketType;
}

export const EarningsTable = ({ days, market }: EarningsTableProps) => {
    if (days.length === 0) {
        return (
            <div className="h-48 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                No prominent earnings scheduled for the week ahead.
            </div>
        );
    }

    return (
        <div className="flex flex-col space-y-8">
            {days.map((day) => (
                <div
                    key={day.date}
                    className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
                >
                    {/* Day header */}
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-800 dark:bg-slate-800/50">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-900 dark:text-slate-100">
                            {formatDateHeader(day.date)}
                        </h3>
                    </div>

                    {/* Data table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-white text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                                <tr>
                                    <th className="px-6 py-3 font-medium">Company</th>
                                    <th className="px-6 py-3 font-medium text-right">
                                        Mkt Cap
                                    </th>
                                    <th className="px-6 py-3 font-medium text-right">
                                        Est. EPS
                                    </th>
                                    <th className="px-6 py-3 font-medium text-right">
                                        Est. Revenue
                                    </th>
                                    <th className="px-6 py-3 font-medium text-center">
                                        Timing
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {day.items.map((item) => {
                                    const timing = formatReportingTime(
                                        item.reportingTime,
                                    );
                                    return (
                                        <tr
                                            key={item.symbol}
                                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                        >
                                            <td className="px-6 py-4">
                                                <a
                                                    href={`/dashboard/stock-info-page?symbol=${encodeURIComponent(
                                                        item.symbol,
                                                    )}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-semibold text-blue-600 hover:underline dark:text-blue-400"
                                                >
                                                    {item.symbol}
                                                </a>
                                                <div className="text-xs text-slate-500 truncate max-w-[200px]">
                                                    {item.name}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right tabular-nums text-slate-700 dark:text-slate-300">
                                                {formatCurrency(
                                                    item.marketCap,
                                                    market,
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right tabular-nums text-slate-700 dark:text-slate-300">
                                                {item.epsEstimated !== null
                                                    ? item.epsEstimated.toFixed(2)
                                                    : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-right tabular-nums text-slate-700 dark:text-slate-300">
                                                {formatCurrency(
                                                    item.revenueEstimated,
                                                    market,
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span
                                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${timing.color}`}
                                                >
                                                    {timing.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
};
